import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {translate} from "../../../translate/translate";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Fab,
    FormControl,
    Grid,
    InputLabel,
    ListItemText,
    MenuItem,
    Paper,
    TextField,
    Typography
} from "@mui/material";
import TablePaginationComponent from "../../../components/table/table-pagination.component";
import {Download, Inventory2Outlined, Search, UnarchiveOutlined} from "@mui/icons-material";
import {PaginatedResult} from "../../../models/default/paginated-result";
import {ErrorComponent} from "../../../components/error/error.component";
import {Field, Form, Formik} from "formik";
import Select from "@mui/material/Select";
import TableActionComponent from "../../../components/table/table-action.component";
import {orderByFn} from "../../../utils/order-by";
import {useFilters} from "../../../filters/base-filter";
import {usePagination} from "../../../utils/pagination";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import {CustomTabPanel} from "../../../utils/custom-tab-panel";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {
    StockAndMaterialOutputFilter,
    StockAndMaterialOutputService
} from "../../../services/stock-and-material-output-service";
import {StockAndMaterialOutput} from "../../../models/basic/stock-and-material-output";
import {ConvertDateToString} from "../../../components/date/date-timeFormat";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {enUS, ptBR} from "date-fns/locale";
import {trackPromise} from "react-promise-tracker";


const StockAndMaterialOutputComponent = () => {
    const service = new StockAndMaterialOutputService();
    const {filters, updateFilter, updateFilterKeepingParam, resetFilters } = useFilters<StockAndMaterialOutputFilter>(
        { plant: "", start_date: "", end_date: "", material_number: ""},
        ["plant", "start_date", "end_date", "material_number"]
    );
	  const expand = [""];
    const [stockAndMaterialOutputList, setStockAndMaterialOutputList] = React.useState<PaginatedResult<StockAndMaterialOutput>>();
    const {
        dataSource,
        pagination,
        resetPage,
        handleChangePage,
        handleChangeRowsPerPage
    } = usePagination<StockAndMaterialOutputFilter>(stockAndMaterialOutputList);

    const [orderDirection, setOrderDirection] = React.useState<'asc' | 'desc' | ''>('asc');

    const [loading, setLoading] = React.useState(false);

    const [tab, setTab] = React.useState(0);

    const [startDate, setStartDate] = React.useState<any>(null);
    const [endDate, setEndDate] = React.useState<any>(null);
    const [invalidDate, setInvalidDate] = useState<boolean>(false);
    const locale = translate.language == "pt" ? ptBR : enUS;

    const [plantList, setPlantList] = React.useState<any[]>([]);
    const [loadingPlant, setLoadingPlant] = React.useState(false);
    const [selectedPlant, setSelectedPlant] = React.useState<any[]>([]);

    const [lastUpdated, setLastUpdated] = React.useState<string>('');
    const [loadingDownload, setLoadingDownload] = React.useState(false);

    const pollingRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        loadLastUpdated();
        loadPlants();
        initializeSelectedPlants();

        if (filters.start_date) {
            setStartDate(new Date(filters.start_date + "T12:00:00"));
        }
        if (filters.end_date) {
            setEndDate(new Date(filters.end_date + "T12:00:00"));
        }
      
        search();
        return () => stopPollingFn();
    }, []);

    useEffect(() => {
        if (!filters.plant && plantList.length) {
            const all = plantList.map((p: any) => p);
            setSelectedPlant(all);
        }
    }, [plantList]);

    const initializeSelectedPlants = () => {
        if (filters.plant) {
            const plants = filters.plant.split(",");
            setSelectedPlant(plants.map((p: any) => p));
        }
    }

    const loadPlants = () => {
        setLoading(true);
        searchPlants();
    }

    const loadLastUpdated = () => {
        service.get_last_updated_sap().then(response => {
             if (!response.data.dt_ultima_atualizacao || response.data.dt_ultima_atualizacao === '-') return "-";

            const dateStr = response.data.dt_ultima_atualizacao.endsWith('Z')
                ? response.data.dt_ultima_atualizacao
                : response.data.dt_ultima_atualizacao + 'Z';

            const date = new Date(dateStr);

            setLastUpdated(date.toLocaleString([], {
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                dateStyle: 'short',
                timeStyle: 'medium'
            }));
        }).catch((error: any) => {
            setLastUpdated(error.response.data.detail)
        });
    }

    const search = (restartIndex = false) => {
        setLoading(true);
        if (tab === 0) {
            searchStock();
        } else {
            searchWithdrawal();
        }
        restartIndex && resetPage();
    };

    const searchStock = () => {
        service.listStock(filters, expand, pagination).then(response => {
            setStockAndMaterialOutputList(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        }).finally(() => {
            setLoading(false);
        });
    }

    const searchPlants = () => {
        setLoadingPlant(true);
        service.listPlants().then(response => {
            setPlantList(response.data.plants);
        }).catch((error: any) => {
            ErrorComponent(error);
        }).finally(() => {
            setLoadingPlant(false);
        });
    }

    const searchWithdrawal = () => {
        service.listWithdrawal(filters, expand, pagination).then(response => {
            setStockAndMaterialOutputList(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        }).finally(() => {
            setLoading(false);
        });
    }

    useEffect(() => {
        search();
    }, [pagination, filters.ordering, tab]);


    const customCellRender = (column: string, index: number, row: StockAndMaterialOutput) => {
        if (column === "plant") {
            return <Typography fontWeight="bold">{row[column]}</Typography>
        }
        return row[column as keyof StockAndMaterialOutput];
    }

    const renderPlants = (selected: any) => {
        if (allPlantSelected()) {
            return translate.t('all');
        }

        return selectedPlant.join(", ");
    }

    const allPlantSelected = () => {
        return selectedPlant.length === plantList.length;
    }

    const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
        resetPage();
        setLoading(true);
        setTab(newValue);
    };

    const handleDateChange = (key: "start_date" | "end_date", date: any) => {
        if (date?.toString().includes("Invalid Date")) {
            setInvalidDate(true);
            return;
        }

        updateFilterKeepingParam(key, date ? ConvertDateToString(date) : "");
        key === "start_date" ? setStartDate(date) : setEndDate(date);

        const compareDate = new Date(filters[key === "start_date" ? "end_date" : "start_date"] + "T12:00:00");
        setInvalidDate(date && compareDate && (key === "start_date" ? date > compareDate : compareDate > date));
    };

    const handleStartDateChange = (date: any) => handleDateChange("start_date", date);
    const handleEndDateChange = (date: any) => handleDateChange("end_date", date);
  
    const export_xlsx = (hash?: string) => {
        search();
        setLoadingDownload(true);

        service.export_xlsx(filters, expand).then(response => {
            if (response.data.hash) {
                get_export_status(response.data.hash);
            }
        }).catch((error: any) => {
            ErrorComponent(error);
            setLoadingDownload(false);
        })
    }

    const get_export_status = (hash: string) => {
        stopPollingFn();
        pollingRef.current = setInterval(() => {
            trackPromise(
                service.get_export_status(hash).then(response => {
                    if (response.data.status === 2 || response.data.status === 3) {
                        stopPollingFn();
                        response.data.status === 2 && download(hash);
                    }
                }).catch((error: any) => {
                    ErrorComponent(error);
                    setLoadingDownload(false);
                })
            )
        }, 5000)
    }

    const download = (hash: string) => {
        service.download({ hash }, '/stock/report/download').then(response => {
            service.downloadFile(response);
        }).finally(() => {
            setLoadingDownload(false);
        });
    }

    const stopPollingFn = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };
  
    return (
        <section>
            <div className="floating-title" style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                <Typography variant={"h5"}>
                    {translate.t("report_stock_and_materia_output")}
                </Typography>
            </div>
            <div className="floating-button" style={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {lastUpdated.toLowerCase().indexOf('erro') != -1 ? (
                    <span
                        className="text"
                        style={{
                            width: '10px',
                            height: '10px',
                            backgroundColor: 'red',
                            borderRadius: '50%',
                            display: 'inline-block',
                            marginRight: '5px'
                        }}
                    />
                ) : (
                    <span
                        className="text"
                        style={{
                            width: '10px',
                            height: '10px',
                            backgroundColor: 'green',
                            borderRadius: '50%',
                            display: 'inline-block',
                            marginRight: '5px'
                        }}
                    />
                )}
                <p className="text">{translate.t("last_updated")}: {lastUpdated}</p>
            </div>

            <Grid size={12}>
                <Paper variant="outlined">
                    <Box p={2}>
                        <Formik
                            enableReinitialize={true}
                            validateOnMount={true}
                            initialValues={{name: ""}}
                            onSubmit={(values, {setSubmitting}) => {
                                setTimeout(() => {
                                    search(true);
                                    setSubmitting(false);
                                }, 500);
                            }}
                        >
                            {
                                () => (
                                    <Form autoComplete="off">
                                        <Box alignItems="center" display="flex" justifyContent="space-between">
                                            <Grid alignItems="center" container spacing={2} direction={"row"}>
                                                <Grid size={2}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>
                                                            {translate.t("plan")}
                                                        </InputLabel>
                                                        <Select
                                                            name="plan"
                                                            label={translate.t("plan")}
                                                            variant={"outlined"}
                                                            fullWidth
                                                            autoFocus={false}
                                                            value={selectedPlant}
                                                            disabled={loadingPlant}
                                                            onChange={(event: any) => {
                                                                if (event.target.value.includes(0)) {
                                                                    if (!allPlantSelected()) {
                                                                        setSelectedPlant(plantList?.map((p: any) => p));
                                                                        updateFilterKeepingParam("plant", plantList?.map((p: any) => p).join(","));
                                                                        return;
                                                                    }
                                                                    setSelectedPlant([]);
                                                                    updateFilterKeepingParam("plant", "");
                                                                    return;
                                                                }
                                                                setSelectedPlant(event.target.value)
                                                                updateFilterKeepingParam("plant", event.target.value.join(","));
                                                            }}
                                                            multiple
                                                            renderValue={(selected) => renderPlants(selected)}
                                                            MenuProps={{
                                                                PaperProps: {
                                                                    style: {
                                                                        maxHeight: '400px',
                                                                    },
                                                                },
                                                            }}
                                                            inputProps={{
                                                                sx: {
                                                                    maxWidth: '400px !important',
                                                                    width: '400px !important',
                                                                }
                                                            }}
                                                        >
                                                            <MenuItem key={0} value={0}>
                                                                <Checkbox checked={allPlantSelected()}/>
                                                                <ListItemText primary={
                                                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                                                        <Typography variant="body1">{translate.t('all')}</Typography>
                                                                        {allPlantSelected() && <Typography variant="body2"
                                                                                                              color="gray">{translate.t('clear')}</Typography>}
                                                                    </Box>
                                                                }/>
                                                            </MenuItem>
                                                            {plantList?.map((p: any) => (
                                                                <MenuItem key={p} value={p}>
                                                                    <Checkbox checked={selectedPlant.includes(p)}/>
                                                                    <ListItemText primary={p}/>
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                { tab === 1 ? (
                                                    <>
                                                        <Grid size={2} minWidth="210px">
                                                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
                                                                <DatePicker
                                                                    label={'Data de retirada (inicial)'}
                                                                    value={startDate}
                                                                    onChange={handleStartDateChange}
                                                                    inputRef={(props: any) => (
                                                                        <TextField
                                                                            {...props}
                                                                            required={false}
                                                                        />
                                                                    )}
                                                                    slotProps={{
                                                                        textField: {
                                                                            fullWidth: true,
                                                                            required: false,
                                                                        }
                                                                    }}
                                                                />
                                                            </LocalizationProvider>
                                                        </Grid>
                                                        <Grid size={2}  minWidth="210px">
                                                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
                                                                <DatePicker
                                                                    label={'Data de retirada (final)'}
                                                                    value={endDate}
                                                                    onChange={handleEndDateChange}
                                                                    inputRef={(props: any) => (
                                                                        <TextField
                                                                            {...props}
                                                                            required={false}
                                                                        />
                                                                    )}
                                                                    slotProps={{
                                                                        textField: {
                                                                            fullWidth: true,
                                                                            required: false,
                                                                        }
                                                                    }}
                                                                    minDate={new Date(filters.start + "T12:00:00") || null}
                                                                />
                                                            </LocalizationProvider>
                                                        </Grid>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Grid size={2} minWidth="210px">
                                                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
                                                                <DatePicker
                                                                    label={'Data inicial'}
                                                                    value={startDate}
                                                                    onChange={handleStartDateChange}
                                                                    inputRef={(props: any) => (
                                                                        <TextField
                                                                            {...props}
                                                                            required={false}
                                                                        />
                                                                    )}
                                                                    slotProps={{
                                                                        textField: {
                                                                            fullWidth: true,
                                                                            required: false,
                                                                        }
                                                                    }}
                                                                />
                                                            </LocalizationProvider>
                                                        </Grid>
                                                        <Grid size={2}  minWidth="210px">
                                                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
                                                                <DatePicker
                                                                    label={'Data final'}
                                                                    value={endDate}
                                                                    onChange={handleEndDateChange}
                                                                    inputRef={(props: any) => (
                                                                        <TextField
                                                                            {...props}
                                                                            required={false}
                                                                        />
                                                                    )}
                                                                    slotProps={{
                                                                        textField: {
                                                                            fullWidth: true,
                                                                            required: false,
                                                                        }
                                                                    }}
                                                                    minDate={new Date(filters.start + "T12:00:00") || null}
                                                                />
                                                            </LocalizationProvider>
                                                        </Grid>
                                                  </>
                                                )}
                                                <Grid size={2} minWidth="400px">
                                                    <Field
                                                        as={TextField}
                                                        name="material_number"
                                                        label={""}
                                                        fullWidth
                                                        variant={"outlined"}
                                                        autoFocus={true}
                                                        value={filters.material_number}
                                                        placeholder={translate.t("search_material")}
                                                        onChange={(event: React.ChangeEvent<{
                                                            value: string
                                                        }>) => updateFilterKeepingParam("material_number", event.target.value)}
                                                    />
                                                </Grid>
                                                <Grid size={1}>
                                                    <Fab
                                                        disabled={invalidDate}
                                                        size="small"
                                                        type="submit"
                                                        color="secondary">
                                                        <Search/>
                                                    </Fab>
                                                </Grid>
                                            </Grid>
                                            <Grid alignItems="center" container justifyContent="flex-end" width="20%">
                                                <Grid >
                                                    <Button
                                                        disabled={loadingDownload}
                                                        color="secondary"
                                                        variant="contained"
                                                        startIcon={<Download />}
                                                        onClick={() => {
                                                            export_xlsx();
                                                        }}>{translate.t("export").toUpperCase()}
                                                        {loadingDownload && <CircularProgress size="20px" sx={{marginLeft: '10px'}} />}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Form>
                                )}
                        </Formik>
                    </Box>
                </Paper>
            </Grid>

            <Box bgcolor="white" margin="1rem auto" pb={1}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tab}
                          onChange={handleChangeTab}>
                        <Tab label={
                            <Grid display="flex" alignItems="center" gap="10px" color="secondary">
                                <Inventory2Outlined/>ESTOQUE
                            </Grid>
                        } />
                        <Tab label={
                            <Grid display="flex" alignItems="center" gap="10px" color="secondary">
                                <UnarchiveOutlined/>SAÍDA
                            </Grid>
                        } />
                    </Tabs>
                </Box>
                <CustomTabPanel value={tab} index={0}>
                    <Paper variant="outlined" sx={{marginTop: "16px"}}>
                        <TablePaginationComponent
                            state={!loading ? dataSource : []}
                            displayedColumnsShow={["Plant", "Material", "Description", "Qty Available", "last_update"]}
                            displayedColumns={["plant", "material_number", "material_description", "stock", "date_last_movement"]}
                            numberColumns={["stock"]}
                            integerColumns={["stock"]}
                            dateColumns={["date_last_movement"]}
                            customCellRender={customCellRender}
                            customColumns={["plant"]}
                            orderByColumns={["plant", "date_last_movement"]}
                            orderBy={filters.ordering}
                            orderByFn={(column: string, orderDirection: any) => {
                                orderByFn(column, orderDirection, filters.ordering || '', setOrderDirection, updateFilterKeepingParam);
                            }}
                            orderDirection={orderDirection}
                            actionsComponent={TableActionComponent}
                            count={stockAndMaterialOutputList?.count}
                            next={stockAndMaterialOutputList?.next}
                            previous={stockAndMaterialOutputList?.previous}
                            page={pagination.page}
                            onPageChange={handleChangePage}
                            rowsPerPage={pagination.limit}
                            handleChangeRowsPerPage={handleChangeRowsPerPage}
                            rowsPerPageOptions={[10, 50, 100]}
                            loading={loading}
                        />
                    </Paper>
                </CustomTabPanel>
                <CustomTabPanel value={tab} index={1}>
                    <Paper variant="outlined" sx={{marginTop: "16px"}}>
                        <TablePaginationComponent
                            state={!loading ? dataSource : []}
                            displayedColumnsShow={["Plant", "Material", "Description", "Withdrawn By", "Quantity", "Withdrawn Date", "Cost Center"]}
                            displayedColumns={["plant", "material_number", "material_description", "withdrawn_by", "removed_quantity", "withdraw_date", "cost_center"]}
                            numberColumns={["removed_quantity"]}
                            integerColumns={["removed_quantity"]}
                            dateColumns={["withdraw_date"]}
                            customCellRender={customCellRender}
                            customColumns={["plant"]}
                            orderByColumns={["plant", "withdraw_date"]}
                            orderBy={filters.ordering}
                            orderByFn={(column: string, orderDirection: any) => {
                                orderByFn(column, orderDirection, filters.ordering || '', setOrderDirection, updateFilterKeepingParam);
                            }}
                            orderDirection={orderDirection}
                            actionsComponent={TableActionComponent}
                            count={stockAndMaterialOutputList?.count}
                            next={stockAndMaterialOutputList?.next}
                            previous={stockAndMaterialOutputList?.previous}
                            page={pagination.page}
                            onPageChange={handleChangePage}
                            rowsPerPage={pagination.limit}
                            handleChangeRowsPerPage={handleChangeRowsPerPage}
                            rowsPerPageOptions={[10, 50, 100]}
                            loading={loading}
                        />
                    </Paper>
                </CustomTabPanel>
            </Box>
        </section>
    );
};

export default StockAndMaterialOutputComponent;
